import {forwardRef, useState} from "react";
import classes from "./form-elements.module.css";

interface XListFormProps {
    children: React.ReactNode[];
    header: string;
    maxRows: number;
    rows: number;
    onChange?: (e: any) => void;
    onFocus?: (e: any) => void;
    onBlur?: (e: any) => void;
    /** Called when the count input loses focus, wherever focus goes next. */
    onCommitRows?: () => void;
}

/**
 * Row-count header plus the rows' fields. The count is a native number input
 * (spinner arrows, bounded 0..maxRows). While it is being edited the box shows
 * what was typed, but only whole numbers reach `onChange`, so clearing the
 * field or a stray character never collapses the rows to zero. On blur the box
 * snaps back to `rows`.
 *
 * The ref lands on the fieldset, the item's wrapper in the library's focus
 * contract: with it registered, focus moving between the count and the row
 * fields no longer counts as leaving the item. The count is still committed
 * on its own blur (`onCommitRows`), as in the old app, so tabbing from the
 * count into the first row reports the new count at once.
 */
export const XListForm = forwardRef<HTMLFieldSetElement, XListFormProps>(
    ({children, header, maxRows, rows, onChange, onFocus, onBlur, onCommitRows}, ref) => {
        const [draft, setDraft] = useState<string | null>(null);
        return (
            <fieldset
                ref={ref}
                className={classes.xListFieldset}
                data-pw="dg-list-form"
                onFocus={onFocus}
                onBlur={onBlur}
            >
                <label className={classes.xListHeader}>
                    <span>{header}</span>
                    <input
                        type="number"
                        className={classes.xListHeaderInput}
                        min={0}
                        max={maxRows}
                        value={draft ?? rows}
                        onChange={(e) => {
                            const {value} = e.target;
                            setDraft(value);
                            if (/^\d+$/.test(value)) {
                                onChange?.({target: {value}});
                            }
                        }}
                        onBlur={() => {
                            setDraft(null);
                            onCommitRows?.();
                        }}
                    />
                </label>
                <div className={classes.xListChildren}>{children}</div>
            </fieldset>
        );
    }
);

XListForm.displayName = "XListForm";
